import React from "react";
import { Link } from "react-router-dom";
import {
  buildMailtoHref,
  buildTelHref,
  formatCurrency,
  formatHotLeadStep,
  formatRelativeTime,
  getInitials,
} from "./chartHelpers";

const buildUserTrackPath = (row) => {
  const userId = row.user?.id;
  if (!userId) return null;
  const params = new URLSearchParams();
  if (row.distinct_id) {
    params.set("posthog_distinct_id", row.distinct_id);
  }
  const query = params.toString();
  return `/analytics-usage/users/${userId}${query ? `?${query}` : ""}`;
};

const displayName = (row) => {
  const name = [row.user?.first_name, row.user?.last_name].filter(Boolean).join(" ");
  if (name) return name;
  if (row.user?.email) return row.user.email;
  return row.distinct_id || "Anonymous user";
};

const HotLeadRow = ({ row }) => {
  const name = displayName(row);
  const trackPath = buildUserTrackPath(row);
  const telHref = buildTelHref(row.user?.mobile, row.user?.mobile_country_calling);
  const mailtoHref = buildMailtoHref(row.user?.email, {
    name: [row.user?.first_name, row.user?.last_name].filter(Boolean).join(" ") || undefined,
    product: row.product_name,
    step: formatHotLeadStep(row.drop_step),
  });
  const initials = getInitials(row.user?.first_name, row.user?.last_name, row.distinct_id || "?");
  const amountClass =
    row.amount >= 10000 ? "analytics-hot-leads__amount--high" : row.amount >= 1000 ? "analytics-hot-leads__amount--mid" : "";

  return (
    <li className="analytics-hot-leads__row">
      <div className="analytics-hot-leads__lead">
        <span className="analytics-hot-leads__avatar" aria-hidden="true">
          {initials}
        </span>
        <div className="analytics-hot-leads__meta">
          <strong>{name}</strong>
          <span>
            {row.product_name} · left at {formatHotLeadStep(row.drop_step)} · {formatRelativeTime(row.last_seen)}
          </span>
        </div>
      </div>
      <div className={`analytics-hot-leads__amount ${amountClass}`.trim()}>
        {row.amount > 0 ? formatCurrency(row.amount) : "—"}
      </div>
      <div className="analytics-hot-leads__actions">
        {telHref ? (
          <a href={telHref} className="analytics-hot-leads__btn analytics-hot-leads__btn--call">
            <i className="fa-solid fa-phone" aria-hidden="true" /> Call
          </a>
        ) : null}
        {mailtoHref ? (
          <a href={mailtoHref} className="analytics-hot-leads__btn analytics-hot-leads__btn--email">
            <i className="fa-regular fa-envelope" aria-hidden="true" /> Email
          </a>
        ) : null}
        {trackPath ? (
          <Link to={trackPath} className="analytics-hot-leads__btn analytics-hot-leads__btn--track">
            {row.action === "retry" ? "Retry" : "Track"} <i className="fa-solid fa-arrow-right" aria-hidden="true" />
          </Link>
        ) : (
          <span className="analytics-hot-leads__btn analytics-hot-leads__btn--disabled" title="Link CRM account via Resync mappings">
            No CRM link
          </span>
        )}
      </div>
    </li>
  );
};

const HotLeadsPanel = ({ hotLeads = {}, onViewDetail, limit = 8, showHeader = true }) => {
  const rows = hotLeads.rows || [];
  const total = hotLeads.total ?? rows.length;
  const visibleRows = limit == null ? rows : rows.slice(0, limit);

  return (
    <section className={`analytics-hot-leads ${showHeader ? "" : "analytics-hot-leads--embedded"}`.trim()}>
      {showHeader ? (
        <div className="analytics-hot-leads__head">
          <div>
            <h3>Hot leads — tapped invest, never finished</h3>
            <p>Updated on refresh · matched CRM contacts only</p>
          </div>
          <div className="analytics-hot-leads__head-right">
            <span className="analytics-hot-leads__badge">Call list · {total}</span>
            {onViewDetail ? (
              <button type="button" className="analytics-view-more" onClick={() => onViewDetail("hot-leads")}>
                View all
              </button>
            ) : null}
          </div>
        </div>
      ) : null}
      {visibleRows.length ? (
        <ul className="analytics-hot-leads__list">
          {visibleRows.map((row) => (
            <HotLeadRow key={row.distinct_id} row={row} />
          ))}
        </ul>
      ) : (
        <p className="analytics-empty">No hot leads in this period — users who started invest without submitting</p>
      )}
    </section>
  );
};

export default HotLeadsPanel;
